/**
 * Skeleton components for Fantasy Points page
 * These MUST match the exact dimensions of the actual components to prevent CLS
 *
 * Key principles:
 * 1. Same row heights (padding + content height)
 * 2. Same column widths (use min-w to ensure consistency)
 * 3. Same number of rows (20 drivers, 10 constructors)
 * 4. Minimal animation (only animate a few elements to reduce jank)
 */

import { Building2, HelpCircle, Search, Users, Zap } from "lucide-react";
import { m } from "@/paraglide/messages";

// Static skeleton placeholder - no animation to reduce jank
function Skeleton({ className = "" }: { className?: string }) {
  return (
    <div className={`bg-muted/40 rounded ${className}`} aria-hidden="true" />
  );
}

// Only the first row gets animation to indicate loading
function AnimatedSkeleton({ className = "" }: { className?: string }) {
  return (
    <div
      className={`animate-pulse bg-muted/50 rounded ${className}`}
      aria-hidden="true"
    />
  );
}

/**
 * Skeleton row for driver fantasy table - matches DriverFantasyTable row exactly
 * @param isFirst - Only animate the first row to reduce jank
 * @param isSprintWeekend - Determines column count
 */
function DriverFantasyRowSkeleton({
  isFirst = false,
  isSprintWeekend = false,
}: {
  isFirst?: boolean;
  isSprintWeekend?: boolean;
}) {
  const SkeletonComponent = isFirst ? AnimatedSkeleton : Skeleton;

  return (
    <tr>
      {/* Driver Code - matches actual cell with team color border */}
      <td className="px-3 py-3 text-center">
        <div className="flex items-center gap-2">
          <SkeletonComponent className="font-mono font-bold text-base px-2 py-1 rounded w-12 h-8" />
        </div>
      </td>

      {/* Total Points */}
      <td className="px-3 py-3 text-center">
        <SkeletonComponent className="h-6 w-10 mx-auto" />
      </td>

      {/* Price */}
      <td className="px-3 py-3 text-center">
        <SkeletonComponent className="h-5 w-10 mx-auto" />
      </td>

      {/* Sprint columns (if sprint weekend) */}
      {isSprintWeekend && (
        <>
          {/* Sprint POS */}
          <td className="px-3 py-3 text-center">
            <SkeletonComponent className="h-5 w-8 mx-auto" />
          </td>
          {/* Sprint PG */}
          <td className="px-3 py-3 text-center">
            <SkeletonComponent className="h-5 w-8 mx-auto" />
          </td>
          {/* Sprint OV */}
          <td className="px-3 py-3 text-center">
            <SkeletonComponent className="h-5 w-8 mx-auto" />
          </td>
          {/* Sprint FL */}
          <td className="px-3 py-3 text-center">
            <SkeletonComponent className="h-5 w-6 mx-auto" />
          </td>
        </>
      )}

      {/* Q POS */}
      <td className="px-3 py-3 text-center">
        <SkeletonComponent className="h-5 w-8 mx-auto" />
      </td>

      {/* Race POS */}
      <td className="px-3 py-3 text-center">
        <SkeletonComponent className="h-5 w-8 mx-auto" />
      </td>

      {/* Race PG */}
      <td className="px-3 py-3 text-center">
        <SkeletonComponent className="h-5 w-8 mx-auto" />
      </td>

      {/* Race OV */}
      <td className="px-3 py-3 text-center">
        <SkeletonComponent className="h-5 w-8 mx-auto" />
      </td>

      {/* Race FL */}
      <td className="px-3 py-3 text-center">
        <SkeletonComponent className="h-5 w-6 mx-auto" />
      </td>

      {/* Race DD */}
      <td className="px-3 py-3 text-center">
        <SkeletonComponent className="h-5 w-6 mx-auto" />
      </td>
    </tr>
  );
}

/**
 * Skeleton row for constructor fantasy table - matches ConstructorFantasyTable row exactly
 * @param isFirst - Only animate the first row to reduce jank
 * @param isSprintWeekend - Determines column count
 */
function ConstructorFantasyRowSkeleton({
  isFirst = false,
  isSprintWeekend = false,
}: {
  isFirst?: boolean;
  isSprintWeekend?: boolean;
}) {
  const SkeletonComponent = isFirst ? AnimatedSkeleton : Skeleton;

  return (
    <tr>
      {/* Constructor Code - matches actual cell with team color border */}
      <td className="px-3 py-3 text-center">
        <div className="flex items-center gap-2">
          <SkeletonComponent className="font-mono font-bold text-base px-2 py-1 rounded w-12 h-8" />
        </div>
      </td>

      {/* Total Points */}
      <td className="px-3 py-3 text-center">
        <SkeletonComponent className="h-6 w-10 mx-auto" />
      </td>

      {/* Price */}
      <td className="px-3 py-3 text-center">
        <SkeletonComponent className="h-5 w-10 mx-auto" />
      </td>

      {/* Sprint columns (if sprint weekend) */}
      {isSprintWeekend && (
        <>
          {/* Sprint POS */}
          <td className="px-3 py-3 text-center">
            <SkeletonComponent className="h-5 w-8 mx-auto" />
          </td>
          {/* Sprint PG */}
          <td className="px-3 py-3 text-center">
            <SkeletonComponent className="h-5 w-8 mx-auto" />
          </td>
          {/* Sprint OV */}
          <td className="px-3 py-3 text-center">
            <SkeletonComponent className="h-5 w-8 mx-auto" />
          </td>
          {/* Sprint FL */}
          <td className="px-3 py-3 text-center">
            <SkeletonComponent className="h-5 w-6 mx-auto" />
          </td>
        </>
      )}

      {/* Q POS */}
      <td className="px-3 py-3 text-center">
        <SkeletonComponent className="h-5 w-8 mx-auto" />
      </td>

      {/* Q TW */}
      <td className="px-3 py-3 text-center">
        <SkeletonComponent className="h-5 w-8 mx-auto" />
      </td>

      {/* Q DQ */}
      <td className="px-3 py-3 text-center">
        <SkeletonComponent className="h-5 w-6 mx-auto" />
      </td>

      {/* Race POS */}
      <td className="px-3 py-3 text-center">
        <SkeletonComponent className="h-5 w-8 mx-auto" />
      </td>

      {/* Race PG */}
      <td className="px-3 py-3 text-center">
        <SkeletonComponent className="h-5 w-8 mx-auto" />
      </td>

      {/* Race OV */}
      <td className="px-3 py-3 text-center">
        <SkeletonComponent className="h-5 w-8 mx-auto" />
      </td>

      {/* Race FL */}
      <td className="px-3 py-3 text-center">
        <SkeletonComponent className="h-5 w-6 mx-auto" />
      </td>

      {/* Race PT */}
      <td className="px-3 py-3 text-center">
        <SkeletonComponent className="h-5 w-6 mx-auto" />
      </td>

      {/* Race FP */}
      <td className="px-3 py-3 text-center">
        <SkeletonComponent className="h-5 w-6 mx-auto" />
      </td>

      {/* Race DNF */}
      <td className="px-3 py-3 text-center">
        <SkeletonComponent className="h-5 w-6 mx-auto" />
      </td>

      {/* Race DQ */}
      <td className="px-3 py-3 text-center">
        <SkeletonComponent className="h-5 w-6 mx-auto" />
      </td>
    </tr>
  );
}

/**
 * Skeleton table for driver fantasy results - matches DriverFantasyTable exactly
 */
export function DriverFantasyTableSkeleton({
  isSprintWeekend = false,
}: {
  isSprintWeekend?: boolean;
}) {
  // Column groups for header
  const getColumnGroups = () => {
    const groups: { name: string; span: number }[] = [];

    // First 3 columns (DR, TOT, Δ$) have no group
    groups.push({ name: "", span: 3 });

    if (isSprintWeekend) {
      // Sprint group (4 columns: POS, PG, OV, FL)
      groups.push({ name: m.f1_table_group_sprint(), span: 4 });
    }

    // Q group (1 column: POS)
    groups.push({ name: m.f1_table_group_q(), span: 1 });

    // Race group (5 columns: POS, PG, OV, FL, DD)
    groups.push({ name: m.f1_table_group_race(), span: 5 });

    return groups;
  };

  const columnGroups = getColumnGroups();

  // Header columns
  const baseHeaders = ["DR", "TOT", "Δ$"];
  const sprintHeaders = isSprintWeekend ? ["POS", "PG", "OV", "FL"] : [];
  const qHeaders = ["POS"];
  const raceHeaders = ["POS", "PG", "OV", "FL", "DD"];
  const allHeaders = [
    ...baseHeaders,
    ...sprintHeaders,
    ...qHeaders,
    ...raceHeaders,
  ];

  // Border indices for group separators
  const getGroupBorderIndices = () => {
    if (isSprintWeekend) {
      return {
        firstInGroup: [3, 7, 8],
        lastInGroup: [6, 7, 12],
      };
    }
    return {
      firstInGroup: [3, 4],
      lastInGroup: [3, 8],
    };
  };

  const borderIndices = getGroupBorderIndices();

  return (
    <section className="rounded-xl border border-border bg-card/50 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-border bg-muted/30">
        <div className="flex items-center gap-3">
          <Users className="h-5 w-5 text-primary" />
          <h3 className="font-display text-lg font-semibold text-foreground">
            {m.f1_apex_drivers()}
          </h3>
        </div>
        <div className="flex items-center gap-3">
          {/* Legend Toggle */}
          <button
            type="button"
            className="flex items-center gap-1 px-3 py-1.5 text-sm rounded-lg bg-muted/50 border border-border"
            disabled
          >
            <HelpCircle className="h-4 w-4" />
            <span>{m.f1_table_legend()}</span>
          </button>
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input
              type="text"
              placeholder={m.f1_table_find_driver()}
              disabled
              className="pl-9 pr-4 py-1.5 text-sm rounded-lg bg-muted/50 border border-border w-64"
            />
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          {/* Column Group Headers */}
          <thead>
            <tr className="border-b border-border bg-muted/10">
              {columnGroups.map((group, idx) => (
                <th
                  key={`group-${group.name || idx}`}
                  colSpan={group.span}
                  className={`px-2 py-2 text-center text-xs font-bold uppercase tracking-wider ${
                    group.name
                      ? "text-primary border-l border-r border-border"
                      : "text-transparent"
                  }`}
                >
                  {group.name || "\u00A0"}
                </th>
              ))}
            </tr>
            {/* Column Headers */}
            <tr className="border-b border-border bg-muted/20">
              {allHeaders.map((header, idx) => {
                const isFirstInGroup = borderIndices.firstInGroup.includes(idx);
                const isLastInGroup = borderIndices.lastInGroup.includes(idx);

                return (
                  <th
                    key={`header-${idx}`}
                    className={`px-3 py-3 text-center text-xs font-mono font-medium text-muted-foreground uppercase tracking-wider ${
                      isFirstInGroup ? "border-l border-border" : ""
                    } ${isLastInGroup ? "border-r border-border" : ""}`}
                  >
                    {header}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody className="divide-y divide-border/50">
            {/* 20 driver rows */}
            {Array.from({ length: 20 }).map((_, i) => (
              <DriverFantasyRowSkeleton
                key={i}
                isFirst={i === 0}
                isSprintWeekend={isSprintWeekend}
              />
            ))}
          </tbody>
        </table>
      </div>

      {/* Footer */}
      <div className="px-6 py-3 border-t border-border bg-muted/20">
        <span className="text-xs font-mono text-muted-foreground">
          20 drivers
        </span>
      </div>
    </section>
  );
}

/**
 * Skeleton table for constructor fantasy results - matches ConstructorFantasyTable exactly
 */
export function ConstructorFantasyTableSkeleton({
  isSprintWeekend = false,
}: {
  isSprintWeekend?: boolean;
}) {
  // Column groups for header
  const getColumnGroups = () => {
    const groups: { name: string; span: number }[] = [];

    // First 3 columns (CR, TOT, Δ$) have no group
    groups.push({ name: "", span: 3 });

    if (isSprintWeekend) {
      // Sprint group (4 columns: POS, PG, OV, FL)
      groups.push({ name: m.f1_table_group_sprint(), span: 4 });
    }

    // Q group (3 columns: POS, TW, DQ)
    groups.push({ name: m.f1_table_group_q(), span: 3 });

    // Race group (8 columns: POS, PG, OV, FL, PT, FP, DNF, DQ)
    groups.push({ name: m.f1_table_group_race(), span: 8 });

    return groups;
  };

  const columnGroups = getColumnGroups();

  // Header columns
  const baseHeaders = ["CR", "TOT", "Δ$"];
  const sprintHeaders = isSprintWeekend ? ["POS", "PG", "OV", "FL"] : [];
  const qHeaders = ["POS", "TW", "DQ"];
  const raceHeaders = ["POS", "PG", "OV", "FL", "PT", "FP", "DNF", "DQ"];
  const allHeaders = [
    ...baseHeaders,
    ...sprintHeaders,
    ...qHeaders,
    ...raceHeaders,
  ];

  // Border indices for group separators
  const getGroupBorderIndices = () => {
    if (isSprintWeekend) {
      return {
        firstInGroup: [3, 7, 10],
        lastInGroup: [6, 9, 17],
      };
    }
    return {
      firstInGroup: [3, 6],
      lastInGroup: [5, 13],
    };
  };

  const borderIndices = getGroupBorderIndices();

  return (
    <section className="rounded-xl border border-border bg-card/50 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-border bg-muted/30">
        <div className="flex items-center gap-3">
          <Building2 className="h-5 w-5 text-primary" />
          <h3 className="font-display text-lg font-semibold text-foreground">
            {m.f1_apex_constructors()}
          </h3>
        </div>
        <div className="flex items-center gap-3">
          {/* Legend Toggle */}
          <button
            type="button"
            className="flex items-center gap-1 px-3 py-1.5 text-sm rounded-lg bg-muted/50 border border-border"
            disabled
          >
            <HelpCircle className="h-4 w-4" />
            <span>{m.f1_table_legend()}</span>
          </button>
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input
              type="text"
              placeholder={m.f1_table_find_constructor()}
              disabled
              className="pl-9 pr-4 py-1.5 text-sm rounded-lg bg-muted/50 border border-border w-64"
            />
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          {/* Column Group Headers */}
          <thead>
            <tr className="border-b border-border bg-muted/10">
              {columnGroups.map((group, idx) => (
                <th
                  key={`group-${group.name || idx}`}
                  colSpan={group.span}
                  className={`px-2 py-2 text-center text-xs font-bold uppercase tracking-wider ${
                    group.name
                      ? "text-primary border-l border-r border-border"
                      : "text-transparent"
                  }`}
                >
                  {group.name || "\u00A0"}
                </th>
              ))}
            </tr>
            {/* Column Headers */}
            <tr className="border-b border-border bg-muted/20">
              {allHeaders.map((header, idx) => {
                const isFirstInGroup = borderIndices.firstInGroup.includes(idx);
                const isLastInGroup = borderIndices.lastInGroup.includes(idx);

                return (
                  <th
                    key={`header-${idx}`}
                    className={`px-3 py-3 text-center text-xs font-mono font-medium text-muted-foreground uppercase tracking-wider ${
                      isFirstInGroup ? "border-l border-border" : ""
                    } ${isLastInGroup ? "border-r border-border" : ""}`}
                  >
                    {header}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody className="divide-y divide-border/50">
            {/* 10 constructor rows */}
            {Array.from({ length: 10 }).map((_, i) => (
              <ConstructorFantasyRowSkeleton
                key={i}
                isFirst={i === 0}
                isSprintWeekend={isSprintWeekend}
              />
            ))}
          </tbody>
        </table>
      </div>

      {/* Footer */}
      <div className="px-6 py-3 border-t border-border bg-muted/20">
        <span className="text-xs font-mono text-muted-foreground">
          10 constructors
        </span>
      </div>
    </section>
  );
}

/**
 * Skeleton for the race info header - matches actual race info display
 */
export function RaceInfoSkeleton({
  isSprintWeekend = false,
}: {
  isSprintWeekend?: boolean;
}) {
  return (
    <div className="text-center">
      <h2 className="font-display text-2xl font-bold flex items-center justify-center gap-2">
        <AnimatedSkeleton className="h-7 w-24" />
        <Skeleton className="h-7 w-48" />
      </h2>
      {isSprintWeekend && (
        <span className="inline-flex items-center gap-1 mt-2 px-3 py-1 bg-primary/20 text-primary text-sm rounded-full">
          <Zap className="h-3 w-3" />
          {m.f1_skeleton_sprint_weekend()}
        </span>
      )}
    </div>
  );
}

/**
 * Full fantasy points content skeleton - matches actual content layout exactly
 */
export function FantasyPointsContentSkeleton({
  isSprintWeekend = false,
}: {
  isSprintWeekend?: boolean;
}) {
  return (
    <div className="container mx-auto px-4 py-8 space-y-8">
      {/* Race Info */}
      <RaceInfoSkeleton isSprintWeekend={isSprintWeekend} />

      {/* Driver Results Table */}
      <DriverFantasyTableSkeleton isSprintWeekend={isSprintWeekend} />

      {/* Constructor Results Table */}
      <ConstructorFantasyTableSkeleton isSprintWeekend={isSprintWeekend} />
    </div>
  );
}
